import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/api';
import QuoteForm from '../components/quotes/QuoteForm';
import { Button, Badge } from '../components/ui';
import { IconRefresh, IconPlus, IconFileTypePdf, IconEdit, IconTrash, IconFileText, IconChevronLeft, IconChevronRight, IconFileExport } from '@tabler/icons-react';
import { showConfirm } from '../utils/confirmUtils';
import ExportModal from '../components/ExportModal';

const normalizeList = (data) => Array.isArray(data) ? data : (data?.items || []);
const toNumber = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

const emptyForm = {
  clientId: '', quoteDate: new Date().toISOString().split('T')[0],
  validUntil: '', status: 'DRAFT', timbreFiscal: 1,
  items: [{ description: '', quantity: 1, priceHT: 0, tvaRate: 19, itemType: 'honoraire' }],
  notes: '',
};

const STATUS_BADGE = {
  DRAFT:    'gray',
  SENT:     'info',
  ACCEPTED: 'success',
  REFUSED:  'danger',
  EXPIRED:  'warning',
  INVOICED: 'gray',
};

const STATUS_LABELS = {
  DRAFT: 'Brouillon', SENT: 'Envoyé', ACCEPTED: 'Accepté',
  REFUSED: 'Refusé', EXPIRED: 'Expiré', INVOICED: 'Facturé',
};

export default function Quotes() {
  const [quotes, setQuotes]     = useState([]);
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  const [showModal, setShowModal]       = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [form, setForm]                 = useState(emptyForm);
  const [showExportModal, setShowExportModal] = useState(false);

  const quoteColumns = [
    { key: 'quoteNumber', label: 'N° Devis', accessor: (qt) => qt?.quoteNumber || '—' },
    { key: 'client', label: 'Client', accessor: (qt) => qt?.clientName || qt?.clientId?.name || '—' },
    { key: 'quoteDate', label: 'Date', accessor: (qt) => qt?.quoteDate ? qt.quoteDate.split('T')[0] : '—' },
    { key: 'validUntil', label: 'Validité', accessor: (qt) => qt?.validUntil ? qt.validUntil.split('T')[0] : '—' },
    { key: 'grandTotal', label: 'Total TTC', accessor: (qt) => `${toNumber(qt?.grandTotal).toFixed(3)} DT` },
    { key: 'status', label: 'Statut', accessor: (qt) => STATUS_LABELS[qt?.status] || qt?.status || '—' },
  ];

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const downloadPDF = async (qt) => {
    try {
      const token       = localStorage.getItem('token');
      const workspaceId = localStorage.getItem('active_workspace_id') || '';
      const res = await fetch('/api/quotes/' + (qt._id || qt.id) + '/pdf', {
        headers: { Authorization: 'Bearer ' + token, 'x-workspace-id': workspaceId }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.message || 'Erreur PDF ' + res.status);
      }
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(new Blob([await blob.arrayBuffer()], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) { setError('Erreur génération PDF : ' + e.message); }
  };

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [qRes, cRes] = await Promise.all([api.get('/quotes'), api.get('/clients')]);
      setQuotes(normalizeList(qRes.data));
      setClients(normalizeList(cRes.data));
    } catch (e) { setError(e?.response?.data?.error || 'Erreur chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return quotes;
    return quotes.filter(qt =>
      (qt?.quoteNumber || '').toLowerCase().includes(q) ||
      (qt?.clientName || qt?.clientId?.name || '').toLowerCase().includes(q) ||
      (qt?.status || '').toLowerCase().includes(q)
    );
  }, [quotes, search]);

  useEffect(() => { setPage(1); }, [quotes.length]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openCreate = () => { setEditingQuote(null); setForm(emptyForm); setError(''); setShowModal(true); };
  const openEdit   = (qt) => {
    setEditingQuote(qt);
    setForm({
      clientId:     qt.clientId?._id || qt.clientId || '',
      quoteDate:    qt.quoteDate ? qt.quoteDate.split('T')[0] : '',
      validUntil:   qt.validUntil ? qt.validUntil.split('T')[0] : '',
      status:       qt.status || 'DRAFT',
      timbreFiscal: qt.timbreFiscal ?? 1,
      items:        qt.items?.length ? qt.items : emptyForm.items,
      notes:        qt.notes || '',
    });
    setError(''); setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setEditingQuote(null); setForm(emptyForm); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const payload = { ...form, timbreFiscal: Number(form.timbreFiscal) || 1 };
      if (editingQuote) await api.put('/quotes/' + (editingQuote._id || editingQuote.id), payload);
      else              await api.post('/quotes', payload);
      closeModal(); await load();
    } catch (e) { setError(e?.response?.data?.error || 'Erreur enregistrement'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (qt) => {
    if (!await showConfirm('Supprimer ce devis ?')) return;
    try { await api.delete('/quotes/' + (qt._id || qt.id)); await load(); }
    catch (e) { setError(e?.response?.data?.error || 'Erreur suppression'); }
  };

  return (
    <div className="flex-1 p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-1">Devis</h1>
        <p className="text-gray-500 text-sm">Créez et suivez vos devis clients.</p>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-800">Liste des devis</h3>
          <p className="text-sm text-gray-500">{filtered.length} devis</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => setShowExportModal(true)} disabled={filtered.length === 0}>
            <IconFileExport size={15} strokeWidth={1.6} className="mr-1" />
            Export
          </Button>
          <Button variant="secondary" size="sm" onClick={load} disabled={loading}>
            <IconRefresh size={15} strokeWidth={1.6} />
            Rafraîchir
          </Button>
          <Button variant="primary" size="sm" onClick={openCreate}>
            <IconPlus size={15} strokeWidth={1.6} />
            Nouveau devis
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 mb-6 p-4">
        <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Rechercher par numéro, client, statut..." />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
            <p className="mt-3 text-gray-500 text-sm">Chargement...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <IconFileText size={32} strokeWidth={1.3} className="text-gray-400" />
            </div>
            <h3 className="text-base font-medium text-gray-700 mb-2">Aucun devis</h3>
            <p className="text-gray-500 mb-6 text-sm">Créez votre premier devis client.</p>
            <Button variant="primary" size="sm" onClick={openCreate}>
              <IconPlus size={15} strokeWidth={1.6} />
              Nouveau devis
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['N° Devis', 'Client', 'Date', 'Validité', 'Total TTC', 'Statut', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginated.map((qt) => (
                  <tr key={qt._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-gray-800 font-semibold">{qt.quoteNumber || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">{qt.clientName || qt.clientId?.name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{qt.quoteDate ? qt.quoteDate.split('T')[0] : '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{qt.validUntil ? qt.validUntil.split('T')[0] : '—'}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">{toNumber(qt.grandTotal).toFixed(3)} DT</td>
                    <td className="px-4 py-3">
                      <Badge variant={STATUS_BADGE[qt.status] || 'gray'}>
                        {STATUS_LABELS[qt.status] || qt.status || '—'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => downloadPDF(qt)} title="Télécharger PDF" type="button"
                          className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 border border-blue-200 transition-colors">
                          <IconFileTypePdf size={14} strokeWidth={1.6} />
                        </button>
                        <button onClick={() => openEdit(qt)} title="Modifier" type="button"
                          className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-50 border border-amber-200 transition-colors">
                          <IconEdit size={14} strokeWidth={1.6} />
                        </button>
                        <button onClick={() => handleDelete(qt)} title="Supprimer" type="button"
                          className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 border border-red-200 transition-colors">
                          <IconTrash size={14} strokeWidth={1.6} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-xs text-gray-500">
              {filtered.length} devis — Page {page}/{totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <IconChevronLeft size={16} strokeWidth={1.6} />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                const p = start + i;
                if (p > totalPages) return null;
                return (
                  <button key={p} onClick={() => setPage(p)}
                    className={`min-w-[32px] px-2 py-1 text-xs font-medium rounded-lg border transition-colors ${p === page ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 text-gray-600 hover:bg-white'}`}>
                    {p}
                  </button>
                );
              })}
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <IconChevronRight size={16} strokeWidth={1.6} />
              </button>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <QuoteForm form={form} setField={setField} clients={clients}
          editingQuote={editingQuote} onSubmit={handleSubmit} onClose={closeModal} loading={loading} />
      )}

      {showExportModal && (
        <ExportModal
          columns={quoteColumns}
          data={filtered}
          title="Liste des devis"
          filename="devis"
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
}
